import { useCallback, useEffect, useRef, useState } from "react";
import CreateCourseAdmin from "./create-course";
import CreateGatewayAdmin from "./create-gateway";
import CreateFerriesAdmin from "./create-ferries";
import CreateTransportAdmin from "./create-transport";
import CreateAccommodationAdmin from "./create-accommodation";
import CreateEmailTemplateAdmin from "./create-template";
import AdminManageBookings from "./manage-bookings";
import CreateCouponAdmin from "./create-coupon";
import EditSiteAdmin from "./edit-site";
import SettingsAdmin from "./settings";
import { useAuth } from "../../Authcontext.jsx";

import {
  generateFerryId,
  createFerry,
  updateFerry,
  deleteFerry,
  getAllFerries,
  uploadFerryImage,
} from "../../services/ferryService";

import {
  generateGatewayId,
  createGateway,
  updateGateway,
  deleteGateway,
  getAllgateway,
  uploadGatewayImage,
} from "../../services/gatewayService";

import {
  generateTransportId,
  createTransport,
  updateTransport,
  deleteTransport,
  getAllTransport,
  uploadTransportImage,
} from "../../services/transportService";

import {
  generateAccommodationId,
  createAccommodation,
  updateAccommodation,
  deleteAccommodation,
  getAllAccommodation,
  uploadAccommodationImage,
} from "../../services/accommodationService";

import {
  generateCourseId,
  createCourse,
  updateCourse,
  deleteCourse,
  getAllCourses,
  uploadCourseImage,
  uploadCourseGalleryImage,
} from "../../services/courseService";

import {
  saveEmailTemplate,
  uploadEmailTemplateImage,
  getEmailTemplates,
} from "../../services/emailService";

import {
  getSiteContent,
  saveDraft as saveSiteContentDraft,
  publish as publishSiteContent,
  uploadSiteContentImage,
} from "../../services/siteContentService";

import { getBookingSettings,updateBookingSettings } from "../../services/settingService";

import {
  createCoupon,
  updateCoupon,
  deleteCoupon,
  getAllCoupons,
  COUPON_SCOPES,
} from "../../services/couponService";

import {
  listCustomPages,
  createCustomPage,
  deleteCustomPage,
} from "../../services/customPagesService";

import {
  getSiteStrings,
  saveStringsDraft,
  publishStrings,
} from "../../services/siteStringsService";

import { Loader2 } from "lucide-react";

import StatusDialog from "@/components/ui/statusloading";

export function CreateFerriesPage() {
  const draftIdRef = useRef(null);
  const getOrCreateDraftId = () => {
    if (!draftIdRef.current) draftIdRef.current = generateFerryId();
    return draftIdRef.current;
  };

  const [dialog, setDialog] = useState({
    open: false,
    variant: "success",
    title: "",
    description: "",
  });

  const showDialog = (variant, title, description) =>
    setDialog({ open: true, variant, title, description });

  const handleSubmit = async (payload) => {
    try {
      const id = getOrCreateDraftId();
      await createFerry(id, payload);
      draftIdRef.current = null; // next "new" session needs a fresh id
      showDialog(
        "success",
        "Ferry created",
        "The ferry was saved successfully.",
      );
    } catch (err) {
      showDialog(
        "error",
        "Failed to create ferry",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUpdate = async (id, payload) => {
    try {
      await updateFerry(id, payload);
      showDialog("success", "Ferry updated", "Your changes were saved.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to update ferry",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleDelete = async (id, imageUrl) => {
    try {
      await deleteFerry(id, imageUrl);
      showDialog("success", "Ferry deleted", "The ferry was removed.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to delete ferry",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleFetchFerry = async () => {
    try {
      return await getAllFerries();
    } catch (err) {
      showDialog(
        "error",
        "Failed to load ferries",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUploadImage = async (file) => {
    try {
      const id = getOrCreateDraftId();
      return await uploadFerryImage(id, file);
    } catch (err) {
      showDialog(
        "error",
        "Image upload failed",
        err.message || "Could not upload image.",
      );
      throw err;
    }
  };

  return (
    <>
      <CreateFerriesAdmin
        onUploadImage={handleUploadImage}
        onSubmit={handleSubmit}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onFetchFerry={handleFetchFerry}
        onCancel={() => {}}
      />
      <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}

export function CreateTransportPage() {
  const draftIdRef = useRef(null);
  const getOrCreateDraftId = () => {
    if (!draftIdRef.current) draftIdRef.current = generateTransportId();
    return draftIdRef.current;
  };

  const [dialog, setDialog] = useState({
    open: false,
    variant: "success",
    title: "",
    description: "",
  });

  const showDialog = (variant, title, description) =>
    setDialog({ open: true, variant, title, description });

  const handleSubmit = async (payload) => {
    try {
      const id = getOrCreateDraftId();
      await createTransport(id, payload);
      draftIdRef.current = null; // next "new" session needs a fresh id
      showDialog(
        "success",
        "Transport created",
        "The transport listing was saved successfully.",
      );
    } catch (err) {
      showDialog(
        "error",
        "Failed to create transport",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUpdate = async (id, payload) => {
    try {
      await updateTransport(id, payload);
      showDialog("success", "Transport updated", "Your changes were saved.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to update transport",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleDelete = async (id, imageUrl) => {
    try {
      await deleteTransport(id, imageUrl);
      showDialog("success", "Transport deleted", "The listing was removed.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to delete transport",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleFetchFerry = async () => {
    try {
      return await getAllTransport();
    } catch (err) {
      showDialog(
        "error",
        "Failed to load transport listings",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUploadImage = async (file) => {
    try {
      const id = getOrCreateDraftId();
      return await uploadTransportImage(id, file);
    } catch (err) {
      showDialog(
        "error",
        "Image upload failed",
        err.message || "Could not upload image.",
      );
      throw err;
    }
  };

  return (
    <>
      <CreateTransportAdmin
        onUploadImage={handleUploadImage}
        onSubmit={handleSubmit}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onFetchFerry={handleFetchFerry}
        onCancel={() => {}}
      />
      <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}

export function CreateAccommodationPage() {
  const draftIdRef = useRef(null);
  const getOrCreateDraftId = () => {
    if (!draftIdRef.current) draftIdRef.current = generateAccommodationId();
    return draftIdRef.current;
  };

  const [dialog, setDialog] = useState({
    open: false,
    variant: "success",
    title: "",
    description: "",
  });

  const showDialog = (variant, title, description) =>
    setDialog({ open: true, variant, title, description });

  const handleSubmit = async (payload) => {
    try {
      const id = getOrCreateDraftId();
      await createAccommodation(id, payload);
      draftIdRef.current = null; // next "new" session needs a fresh id
      showDialog(
        "success",
        "Accommodation created",
        "The listing was saved successfully.",
      );
    } catch (err) {
      showDialog(
        "error",
        "Failed to create accommodation",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUpdate = async (id, payload) => {
    try {
      await updateAccommodation(id, payload);
      showDialog(
        "success",
        "Accommodation updated",
        "Your changes were saved.",
      );
    } catch (err) {
      showDialog(
        "error",
        "Failed to update accommodation",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleDelete = async (id, imageUrl) => {
    try {
      await deleteAccommodation(id, imageUrl);
      showDialog(
        "success",
        "Accommodation deleted",
        "The listing was removed.",
      );
    } catch (err) {
      showDialog(
        "error",
        "Failed to delete accommodation",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleFetchFerry = async () => {
    try {
      return await getAllAccommodation();
    } catch (err) {
      showDialog(
        "error",
        "Failed to load properties",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUploadImage = async (file) => {
    try {
      const id = getOrCreateDraftId();
      return await uploadAccommodationImage(id, file);
    } catch (err) {
      showDialog(
        "error",
        "Image upload failed",
        err.message || "Could not upload image.",
      );
      throw err;
    }
  };

  return (
    <>
      <CreateAccommodationAdmin
        onUploadImage={handleUploadImage}
        onSubmit={handleSubmit}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onFetchFerry={handleFetchFerry}
        onCancel={() => {}}
      />
      <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}

export function CreateCoursePage() {
  const draftIdRef = useRef(null);
  const getOrCreateDraftId = () => {
    if (!draftIdRef.current) draftIdRef.current = generateCourseId();
    return draftIdRef.current;
  };

  const [ferryRoutes, setFerryRoutes] = useState([]);
  const [transportOptions, setTransportOptions] = useState([]);
  const [accommodations, setAccommodations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dialog, setDialog] = useState({
    open: false,
    variant: "success",
    title: "",
    description: "",
  });

  const showDialog = (variant, title, description) =>
    setDialog({ open: true, variant, title, description });

  useEffect(() => {
    (async () => {
      try {
        const [ferries, transport, accommodation] = await Promise.all([
          getAllFerries(),
          getAllTransport(),
          getAllAccommodation(),
        ]);
        setFerryRoutes(ferries);
        setTransportOptions(transport);
        setAccommodations(accommodation);
      } catch (err) {
        console.error("Failed to load recommendation options:", err);
        setLoadError(err?.message || "Failed to load travel options.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async (payload) => {
    try {
      const id = getOrCreateDraftId();
      await createCourse(id, payload);
      draftIdRef.current = null; // next "new" session needs a fresh id
      showDialog(
        "success",
        "Course created",
        "The course was saved successfully.",
      );
    } catch (err) {
      showDialog(
        "error",
        "Failed to create course",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUpdate = async (id, payload) => {
    try {
      await updateCourse(id, payload);
      showDialog("success", "Course updated", "Your changes were saved.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to update course",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleDelete = async (id, imageUrl) => {
    try {
      await deleteCourse(id, imageUrl);
      showDialog("success", "Course deleted", "The course was removed.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to delete course",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleFetchCourses = async () => {
    try {
      return await getAllCourses();
    } catch (err) {
      showDialog(
        "error",
        "Failed to load courses",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUploadImage = async (file) => {
    try {
      const id = getOrCreateDraftId();
      return await uploadCourseImage(id, file);
    } catch (err) {
      showDialog(
        "error",
        "Image upload failed",
        err.message || "Could not upload image.",
      );
      throw err;
    }
  };

  const handleUploadGalleryImage = async (file) => {
    try {
      const id = getOrCreateDraftId();
      return await uploadCourseGalleryImage(id, file);
    } catch (err) {
      showDialog(
        "error",
        "Gallery image upload failed",
        err.message || "Could not upload image.",
      );
      throw err;
    }
  };

  // Was `return null` — showed a blank white screen with no feedback while
  // the ferry/transport/accommodation lists loaded. A spinner tells the
  // admin the page is actually doing something.
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8F7] flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-8 text-center text-red-600">
        Failed to load this page: {loadError}
      </div>
    );
  }

  return (
    <>
      <CreateCourseAdmin
        onSubmit={handleSubmit}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onFetchCourses={handleFetchCourses}
        onUploadImage={handleUploadImage}
        onUploadGalleryImage={handleUploadGalleryImage}
        ferryRoutes={ferryRoutes}
        transportOptions={transportOptions}
        accommodations={accommodations}
      />
      <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}

export function CreateGatewayPage() {
  const [gateways, setGateways] = useState([]);
  const [packages, setPackages] = useState([]); // now holds courses, not packages
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dialog, setDialog] = useState({
    open: false,
    variant: "success",
    title: "",
    description: "",
  });

  const showDialog = (variant, title, description) =>
    setDialog({ open: true, variant, title, description });

  // Holds the id for an in-progress "new gateway" session, same pattern
  // as generatePackageId — reused across image upload + final submit,
  // reset once the create actually succeeds.
  const draftIdRef = useRef(null);
  const getOrCreateDraftId = () => {
    if (!draftIdRef.current) draftIdRef.current = generateGatewayId();
    return draftIdRef.current;
  };

  const refreshGateways = useCallback(async () => {
    setGateways(await getAllgateway());
  }, []);

  // This was missing entirely — loading never resolved, so the page
  // rendered `null` on every load regardless of what data existed.
  useEffect(() => {
    (async () => {
      try {
        const [gwList, courseList] = await Promise.all([
          getAllgateway(),
          getAllCourses(),
        ]);
        setGateways(gwList);
        setPackages(courseList);
      } catch (err) {
        console.error("Failed to load gateways/courses:", err);
        setLoadError(err?.message || "Failed to load data.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // file, existingUrl (for replace-cleanup), editingId (null when creating)
  const handleUploadImage = async (file, existingUrl, editingId) => {
    try {
      const id = editingId || getOrCreateDraftId();
      return await uploadGatewayImage(id, file, existingUrl);
    } catch (err) {
      showDialog(
        "error",
        "Image upload failed",
        err.message || "Could not upload image.",
      );
      throw err;
    }
  };

  const handleSubmit = async (payload) => {
    try {
      const id = getOrCreateDraftId();
      await createGateway(id, payload); // matches the 2-arg service signature
      draftIdRef.current = null; // next "new" session needs a fresh id
      await refreshGateways();
      showDialog(
        "success",
        "Gateway created",
        "The gateway was saved successfully.",
      );
    } catch (err) {
      showDialog(
        "error",
        "Failed to create gateway",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUpdate = async (id, payload) => {
    try {
      await updateGateway(id, payload);
      await refreshGateways();
      showDialog("success", "Gateway updated", "Your changes were saved.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to update gateway",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleDelete = async (id, imageUrl) => {
    try {
      await deleteGateway(id, imageUrl);
      await refreshGateways();
      showDialog("success", "Gateway deleted", "The gateway was removed.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to delete gateway",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  if (loading) return null; // swap for a real loading state if you have one

  if (loadError) {
    return (
      <div className="p-8 text-center text-red-600">
        Failed to load this page: {loadError}
      </div>
    );
  }

  return (
    <>
      <CreateGatewayAdmin
        gateways={gateways}
        golfPackages={packages}
        onUploadImage={handleUploadImage}
        onSubmit={handleSubmit}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onCancel={() => {}}
      />
      <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}

export function CreateTemplatePage() {
  const [dialog, setDialog] = useState({
    open: false,
    variant: "success",
    title: "",
    description: "",
  });
  const showDialog = (variant, title, description) =>
    setDialog({ open: true, variant, title, description });

  const handleFetchTemplates = useCallback(async () => {
    try {
      return await getEmailTemplates();
    } catch (err) {
      showDialog(
        "error",
        "Failed to load templates",
        err.message || "Could not load email templates.",
      );
      throw err;
    }
  }, []);

  const handleSaveTemplate = async (templateId, payload) => {
    try {
      await saveEmailTemplate(templateId, payload);
      showDialog("success", "Template saved", "Your changes were saved.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to save template",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUploadImage = async (file, templateId) => {
    try {
      return await uploadEmailTemplateImage(file, templateId);
    } catch (err) {
      showDialog(
        "error",
        "Image upload failed",
        err.message || "Could not upload image.",
      );
      throw err;
    }
  };

  return (
    <>
      <CreateEmailTemplateAdmin
        onFetchTemplates={handleFetchTemplates}
        onSaveTemplate={handleSaveTemplate}
        onUploadImage={handleUploadImage}
      />
      <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}

export function AdminManageBookingsPage() {
  return (
    <>
      <AdminManageBookings />
    </>
  );
}

export function CreateCouponPage() {
  const [dialog, setDialog] = useState({
    open: false,
    variant: "success",
    title: "",
    description: "",
  });
  const showDialog = (variant, title, description) =>
    setDialog({ open: true, variant, title, description });

  const handleFetchTemplates = useCallback(async () => {
    try {
      return await getAllCoupons();
    } catch (err) {
      showDialog(
        "error",
        "Failed to load coupons",
        err.message || "Could not load coupons.",
      );
      throw err;
    }
  }, []);

  const handleSaveTemplate = async (payload) => {
    try {
      await createCoupon(payload);
      showDialog("success", "Coupon created", "Your new coupon was created.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to create coupon",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUpdateTemplate = async (id, payload) => {
    try {
      await updateCoupon(id, payload);
      showDialog("success", "Coupon updated", "Your coupon was updated.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to update coupon",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleDeleteCoupon = async (id) => {
    try {
      await deleteCoupon(id);
      showDialog("success", "Coupon deleted", "Your coupon was deleted.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to delete coupon",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  return (
    <>
      <CreateCouponAdmin
        createCoupon={handleSaveTemplate}
        updateCoupon={handleUpdateTemplate}
        deleteCoupon={handleDeleteCoupon}
        getAllCoupons={handleFetchTemplates}
        COUPON_SCOPES={COUPON_SCOPES}
      />
      <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}

export function EditSitePage() {
  const { user } = useAuth();
  const [dialog, setDialog] = useState({
    open: false,
    variant: "success",
    title: "",
    description: "",
  });

  const showDialog = (variant, title, description) =>
    setDialog({ open: true, variant, title, description });

  const handleFetchPageContent = async (pageKey) => {
    try {
      return await getSiteContent(pageKey);
    } catch (err) {
      showDialog(
        "error",
        "Failed to load page",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleSaveDraft = async (pageKey, blocks) => {
    try {
      await saveSiteContentDraft(pageKey, blocks);
      showDialog(
        "success",
        "Draft saved",
        "Your changes were saved as a draft.",
      );
    } catch (err) {
      showDialog(
        "error",
        "Failed to save draft",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handlePublish = async (pageKey, blocks) => {
    try {
      await publishSiteContent(pageKey, blocks, user?.uid);
      showDialog("success", "Published", "This page is now live.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to publish",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleUploadImage = async (pageKey, file) => {
    try {
      return await uploadSiteContentImage(pageKey, file);
    } catch (err) {
      showDialog(
        "error",
        "Image upload failed",
        err.message || "Could not upload image.",
      );
      throw err;
    }
  };

  const handleListCustomPages = async () => {
    try {
      return await listCustomPages();
    } catch (err) {
      showDialog(
        "error",
        "Failed to load custom pages",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleCreateCustomPage = async (slug, title) => {
    try {
      await createCustomPage(slug, title);
    } catch (err) {
      showDialog(
        "error",
        "Failed to create page",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleDeleteCustomPage = async (slug) => {
    try {
      await deleteCustomPage(slug);
      showDialog("success", "Page deleted", "The page was removed.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to delete page",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleFetchStrings = async () => {
    try {
      return await getSiteStrings();
    } catch (err) {
      showDialog(
        "error",
        "Failed to load text",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handleSaveStringsDraft = async (values) => {
    try {
      await saveStringsDraft(values);
      showDialog(
        "success",
        "Draft saved",
        "Your changes were saved as a draft.",
      );
    } catch (err) {
      showDialog(
        "error",
        "Failed to save draft",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const handlePublishStrings = async (values) => {
    try {
      await publishStrings(values, user?.uid);
      showDialog("success", "Published", "This text is now live.");
    } catch (err) {
      showDialog(
        "error",
        "Failed to publish",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  return (
    <>
      <EditSiteAdmin
        onFetchPageContent={handleFetchPageContent}
        onSaveDraft={handleSaveDraft}
        onPublish={handlePublish}
        onUploadImage={handleUploadImage}
        onListCustomPages={handleListCustomPages}
        onCreateCustomPage={handleCreateCustomPage}
        onDeleteCustomPage={handleDeleteCustomPage}
        onFetchStrings={handleFetchStrings}
        onSaveStringsDraft={handleSaveStringsDraft}
        onPublishStrings={handlePublishStrings}
      />
      <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}

export function SettingsPage() {
  const { user } = useAuth();
  const [dialog, setDialog] = useState({
    open: false,
    variant: "success",
    title: "",
    description: "",
  });

  const showDialog = (variant, title, description) =>
    setDialog({ open: true, variant, title, description });

  const handleFetch = async () => {
    try {
      return await getBookingSettings();
    } catch (err) {
      showDialog(
        "error",
        "Failed to load page",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  const updatevalues = async (form) => {
    try {
      await updateBookingSettings(form);
    } catch (err) {
      showDialog(
        "error",
        "Failed to Update",
        err.message || "Something went wrong.",
      );
      throw err;
    }
  };

  return (
    <>
      <SettingsAdmin 
         onFetch={handleFetch} 
         onUpdate={updatevalues}
      />

        <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}
